/**
 * PoolTable3DViewer Component
 * Three.js-based 3D visualization of pool table with projector position
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export class PoolTable3DViewer {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' 
      ? document.querySelector(container) 
      : container;
    
    this.options = {
      width: options.width || 800,
      height: options.height || 400,
      ...options
    };
    
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.tableGroup = null;
    this.projectorMesh = null;
    this.tableFootprint = null;
    this.feltFootprint = null;
    this.axisLabelsGroup = null;
    this.animationFrameId = null;
    this.interactionActive = false;
    this.feltMesh = null;
    // Load felt color from localStorage or use default
    const storedColor = localStorage.getItem('poolTableFeltColor');
    this.feltColor = storedColor ? parseInt(storedColor, 16) : 0x2d5016; // Default dark green felt color
    
    // Parameters (in feet/inches, converted to meters for Three.js)
    this.params = {
      tableLength: null,      // feet
      tableWidth: null,       // feet
      tableHeightInches: null, // inches
      ceilingHeightInches: null, // inches
      mountingOption: null,   // 'ceiling', 'side', or 'end'
    };
    
    this.init();
  }
  
  init() {
    this.render();
    try {
      this.setupThreeJS();
      this.animate();
    } catch (error) {
      console.error('Error initializing 3D viewer:', error);
      this.showError(error);
    }
  }
  
  render() {
    this.container.innerHTML = `
      <div class="pool-table-3d-viewer-container">
        <div id="threejs-canvas-container" style="width: 100%; max-height: 100%; aspect-ratio: 1 / 1; position: relative; background: #f5f5f5; border-radius: 8px; overflow: hidden;">
          <canvas id="threejs-canvas" style="width: 100%; height: 100%; display: block;"></canvas>
          <div id="viewer-overlay" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.02); display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 10; transition: background 0.2s;">
            <div id="viewer-activate-hint" style="background: rgba(255, 255, 255, 0.95); padding: 0.75rem 1.5rem; border-radius: 0.5rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); font-size: 0.875rem; color: #333; pointer-events: none;">
              Tap to interact • Scroll to zoom
            </div>
          </div>
          <div id="felt-color-picker-widget" style="position: absolute; top: 12px; left: 12px; z-index: 20;">
            <button id="felt-color-button" style="background: rgba(255, 255, 255, 0.95); border: 1px solid rgba(0, 0, 0, 0.1); border-radius: 6px; padding: 0.5rem 0.75rem; cursor: pointer; font-size: 0.75rem; color: #333; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); transition: all 0.2s; display: flex; align-items: center; gap: 0.5rem; position: relative;">
              <span id="felt-color-swatch" style="width: 20px; height: 20px; border-radius: 4px; border: 1px solid rgba(0, 0, 0, 0.2); background-color: #2d5016; flex-shrink: 0;"></span>
              <span>Felt color, click to change</span>
              <input type="color" id="felt-color-input" value="#2d5016" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; z-index: 1; border: none; padding: 0; margin: 0;">
            </button>
          </div>
        </div>
        <div class="viewer-hint" id="viewer-status-hint" style="text-align: center; margin-top: 0.5rem; font-size: 0.75rem; color: #666;">
          Tap viewer to interact
        </div>
      </div>
    `;
  }
  
  showError(error) {
    const container = this.container.querySelector('#threejs-canvas-container');
    if (container) {
      container.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; height: 100%; flex-direction: column; padding: 2rem; color: #666;">
          <div style="font-size: 2rem; margin-bottom: 1rem;">⚠️</div>
          <div style="font-weight: 600; margin-bottom: 0.5rem; color: #333;">3D Viewer Error</div>
          <div style="font-size: 0.875rem; text-align: center; max-width: 400px;">
            ${error?.message || 'An error occurred while loading the 3D preview. Please refresh the page.'}
          </div>
        </div>
      `;
    }
  }
  
  setupThreeJS() {
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      try {
        const container = this.container.querySelector('#threejs-canvas-container');
        if (!container) {
          throw new Error('Three.js container not found');
        }
        this._setupThreeJSInternal(container);
      } catch (error) {
        console.error('Error setting up Three.js:', error);
        this.showError(error);
      }
    }, 0);
  }
  
  _setupThreeJSInternal(container) {
    // Scene setup
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf5f5f5);
    
    // Get container dimensions for aspect ratio (square)
    const containerEl = this.container.querySelector('#threejs-canvas-container');
    const containerWidth = containerEl ? containerEl.clientWidth : this.options.width;
    const containerHeight = containerEl ? containerEl.clientHeight : containerWidth; // Square aspect ratio
    
    // Camera setup (perspective camera)
    this.camera = new THREE.PerspectiveCamera(
      50, // FOV
      containerWidth / containerHeight,
      0.1, // near
      1000 // far
    );
    this.camera.position.set(12, 8, 12);
    this.camera.lookAt(0, 0, 0);
    
    // Renderer setup
    const canvas = this.container.querySelector('#threejs-canvas');
    this.renderer = new THREE.WebGLRenderer({ 
      canvas: canvas,
      antialias: true 
    });
    this.renderer.setSize(containerWidth, containerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    
    // Soft, cartoony lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    this.scene.add(ambientLight);
    
    // Soft directional light from above
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6);
    directionalLight.position.set(5, 10, 5);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 1024;
    directionalLight.shadow.mapSize.height = 1024;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 50;
    directionalLight.shadow.camera.left = -10;
    directionalLight.shadow.camera.right = 10;
    directionalLight.shadow.camera.top = 10;
    directionalLight.shadow.camera.bottom = -10;
    directionalLight.shadow.radius = 4; // Soft shadows
    this.scene.add(directionalLight);
    
    // Additional fill light for softness
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
    fillLight.position.set(-5, 5, -5);
    this.scene.add(fillLight);
    
    // Grid helper (floor grid) - subtle
    const gridHelper = new THREE.GridHelper(20, 20, 0xcccccc, 0xe0e0e0);
    gridHelper.position.y = -0.01;
    this.scene.add(gridHelper);
    
    // Create axis labels
    this.createAxisLabels();
    
    // OrbitControls for camera rotation
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.minDistance = 5;
    this.controls.maxDistance = 30;
    this.controls.maxPolarAngle = Math.PI / 2 - 0.1; // Prevent going below ground
    
    // Initially disable controls - require tap to activate
    this.controls.enabled = false;
    
    // Setup tap-to-activate interaction
    this.setupInteractionMode();
    
    // Setup felt color picker
    this.setupFeltColorPicker();
    
    // Handle window resize
    this.handleResize = () => {
      const containerEl = this.container.querySelector('#threejs-canvas-container');
      if (containerEl) {
        const width = containerEl.clientWidth;
        const height = containerEl.clientHeight; // Use actual container height (constrained by aspect-ratio)
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
      }
    };
    window.addEventListener('resize', this.handleResize);
    this.handleResize(); // Initial resize
    
    // Build initial scene
    this.updateScene();
  }
  
  setupFeltColorPicker() {
    const colorButton = this.container.querySelector('#felt-color-button');
    const colorInput = this.container.querySelector('#felt-color-input');
    const colorSwatch = this.container.querySelector('#felt-color-swatch');
    
    if (!colorButton || !colorInput || !colorSwatch) return;
    
    // Convert hex color to string for input and swatch
    const hexColor = '#' + this.feltColor.toString(16).padStart(6, '0');
    colorInput.value = hexColor;
    colorSwatch.style.backgroundColor = hexColor;
    
    // Stop propagation on both button and input to prevent viewer interaction
    colorButton.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    colorInput.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    // Update felt color when color input changes
    colorInput.addEventListener('input', (e) => {
      const hexValue = e.target.value;
      // Convert hex string to number (remove # and parse)
      this.feltColor = parseInt(hexValue.substring(1), 16);
      
      // Save to localStorage
      localStorage.setItem('poolTableFeltColor', hexValue.substring(1));
      
      // Update the swatch color
      colorSwatch.style.backgroundColor = hexValue;
      
      // Update the felt mesh material if it exists
      if (this.feltMesh && this.feltMesh.material) {
        this.feltMesh.material.color.setHex(this.feltColor);
      }
      
      // Update the felt footprint color if it exists
      if (this.feltFootprint && this.feltFootprint.material) {
        this.feltFootprint.material.color.setHex(this.feltColor);
      }
    });
  }
  
  setupInteractionMode() {
    const overlay = this.container.querySelector('#viewer-overlay');
    const statusHint = this.container.querySelector('#viewer-status-hint');
    const canvas = this.renderer.domElement;
    
    if (!overlay || !this.controls) return;
    
    // Initially disable canvas pointer events to allow page scrolling
    canvas.style.pointerEvents = 'none';
    canvas.style.touchAction = 'none';
    
    // Track last interaction time
    let lastInteractionTime = 0;
    let interactionTimeout = null;
    
    // Function to activate interaction
    const activateInteraction = (e) => {
      if (this.interactionActive) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      this.interactionActive = true;
      this.controls.enabled = true;
      lastInteractionTime = Date.now();
      
      // Hide overlay with fade
      overlay.style.transition = 'opacity 0.2s ease';
      overlay.style.opacity = '0';
      overlay.style.pointerEvents = 'none';
      setTimeout(() => {
        overlay.style.display = 'none';
      }, 200);
      
      // Enable canvas interaction
      canvas.style.pointerEvents = 'auto';
      
      // Update status hint
      if (statusHint) {
        statusHint.textContent = 'Interaction active • Drag to rotate • Scroll to zoom';
        statusHint.style.color = '#3b82f6';
      }
      
      // Reset inactivity timeout
      resetInactivityTimeout();
    };
    
    // Function to deactivate after inactivity
    const resetInactivityTimeout = () => {
      if (interactionTimeout) {
        clearTimeout(interactionTimeout);
      }
      
      interactionTimeout = setTimeout(() => {
        if (this.interactionActive) {
          this.controls.enabled = false;
          this.interactionActive = false;
          lastInteractionTime = 0;
          
          // Show overlay again
          overlay.style.display = 'flex';
          overlay.style.opacity = '1';
          overlay.style.pointerEvents = 'auto';
          
          // Disable canvas interaction
          canvas.style.pointerEvents = 'none';
          
          // Update status hint
          if (statusHint) {
            statusHint.textContent = 'Tap viewer to interact';
            statusHint.style.color = '#666';
          }
        }
      }, 5000); // 5 seconds of inactivity
    };
    
    // Activate on tap/click of overlay
    overlay.addEventListener('click', activateInteraction);
    overlay.addEventListener('touchend', (e) => {
      activateInteraction(e);
    }, { passive: false });
    
    // Track user interaction with controls
    const trackInteraction = () => {
      if (this.interactionActive) {
        lastInteractionTime = Date.now();
        resetInactivityTimeout();
      }
    };
    
    // Monitor control changes
    canvas.addEventListener('mousedown', trackInteraction);
    canvas.addEventListener('touchstart', trackInteraction, { passive: true });
    canvas.addEventListener('wheel', trackInteraction, { passive: true });
    
    // Prevent page scroll when actively interacting with canvas
    // But allow scrolling when interaction is not active
    let touchStartY = 0;
    canvas.addEventListener('touchstart', (e) => {
      if (this.interactionActive && e.touches.length === 1) {
        touchStartY = e.touches[0].clientY;
      }
    }, { passive: true });
    
    canvas.addEventListener('touchmove', (e) => {
      if (!this.interactionActive) {
        // Allow page scroll when interaction is not active
        return;
      }
      
      // When interaction is active, prevent default to allow 3D rotation
      // But only if it's a single touch (rotation gesture)
      if (e.touches.length === 1) {
        e.preventDefault();
      }
    }, { passive: false });
  }
  
  // Convert feet to meters (Three.js uses meters)
  feetToMeters(feet) {
    return feet * 0.3048;
  }
  
  // Convert inches to meters
  inchesToMeters(inches) {
    return inches * 0.0254;
  }
  
  createAxisLabels() {
    if (!this.scene) return;
    
    this.axisLabelsGroup = new THREE.Group();
    
    // Create labels every foot along X and Z axes
    // X axis labels (along the length dimension)
    for (let feet = -10; feet <= 10; feet++) {
      if (feet === 0) continue; // Skip origin
      
      const label = this.createLabel(feet.toString());
      label.position.set(this.feetToMeters(feet), 0.05, 0);
      this.axisLabelsGroup.add(label);
    }
    
    // Z axis labels (along the width dimension)
    for (let feet = -10; feet <= 10; feet++) {
      if (feet === 0) continue; // Skip origin
      
      const label = this.createLabel(feet.toString());
      label.position.set(0, 0.05, this.feetToMeters(feet));
      // Rotate 90 degrees to align with Z axis
      label.rotation.y = Math.PI / 2;
      this.axisLabelsGroup.add(label);
    }
    
    // Origin label (0, 0)
    const originLabel = this.createLabel('0');
    originLabel.position.set(0, 0.05, 0);
    this.axisLabelsGroup.add(originLabel);
    
    this.scene.add(this.axisLabelsGroup);
  }
  
  createLabel(text) {
    // Create canvas for text
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const size = 128; // Canvas size
    canvas.width = size;
    canvas.height = size;
    
    // Clear canvas with transparent background
    context.clearRect(0, 0, size, size);
    
    // Draw text
    context.fillStyle = '#333333';
    context.font = 'Bold 48px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, size / 2, size / 2);
    
    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    
    // Create sprite material (always faces camera)
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.1
    });
    
    // Create sprite
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(0.5, 0.5, 1); // Constant size in world units
    
    return sprite;
  }
  
  updateScene() {
    if (!this.scene) return;
    
    try {
      // Clear existing meshes
      if (this.tableGroup) {
        this.scene.remove(this.tableGroup);
        this.tableGroup.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
        this.tableGroup = null;
        this.feltMesh = null; // Clear felt mesh reference
      }
      
      if (this.projectorMesh) {
        this.scene.remove(this.projectorMesh);
        if (this.projectorMesh.geometry) this.projectorMesh.geometry.dispose();
        if (this.projectorMesh.material) this.projectorMesh.material.dispose();
        this.projectorMesh = null;
      }
      
      // Clear footprint outlines (we'll recreate them if needed)
      if (this.tableFootprint) {
        this.scene.remove(this.tableFootprint);
        if (this.tableFootprint.geometry) this.tableFootprint.geometry.dispose();
        if (this.tableFootprint.material) this.tableFootprint.material.dispose();
        this.tableFootprint = null;
      }
      if (this.feltFootprint) {
        this.scene.remove(this.feltFootprint);
        if (this.feltFootprint.geometry) this.feltFootprint.geometry.dispose();
        if (this.feltFootprint.material) this.feltFootprint.material.dispose();
        this.feltFootprint = null;
      }
      
      // Note: axisLabelsGroup is not cleared - it stays constant
      
      // Check what we have
      const hasLengthWidth = this.params.tableLength && 
                            this.params.tableWidth &&
                            !isNaN(this.params.tableLength) && 
                            !isNaN(this.params.tableWidth);
      
      const hasTableDimensions = hasLengthWidth && 
                                 this.params.tableHeightInches &&
                                 !isNaN(this.params.tableHeightInches);
      
      // Always show footprint when we have length/width
      if (hasLengthWidth) {
        this.createTableFootprint();
      }
      
      if (hasTableDimensions) {
        // Create full 3D pool table
        this.createPoolTable();
        
        // Create projector if we have ceiling height and mounting option
        if (this.params.ceilingHeightInches && this.params.mountingOption && !isNaN(this.params.ceilingHeightInches)) {
          this.createProjector();
        }
        
        // Update camera to frame the scene
        this.frameScene();
      } else if (hasLengthWidth) {
        // Only footprint - frame the footprint
        this.frameFootprint();
      } else {
        // No table yet - reset camera to default view showing the grid
        this.resetCameraToDefault();
      }
    } catch (error) {
      console.error('Error updating scene:', error);
      this.showError(error);
    }
  }
  
  createTableFootprint() {
    if (!this.scene) return;
    
    try {
      // Felt dimensions (playing surface)
      const feltLength = this.feetToMeters(this.params.tableLength);
      const feltWidth = this.feetToMeters(this.params.tableWidth);
      
      // Rail dimensions (same as in createPoolTable)
      const railWidth = 0.08; // 8cm rail width
      
      // Overall table dimensions (felt + rails)
      const tableLength = feltLength + railWidth * 2;
      const tableWidth = feltWidth + railWidth * 2;
      
      // Validate dimensions
      if (isNaN(feltLength) || isNaN(feltWidth) || feltLength <= 0 || feltWidth <= 0) {
        console.warn('Invalid footprint dimensions:', { feltLength, feltWidth });
        return;
      }
      
      // Helper function to create a rectangular footprint outline
      const createFootprintOutline = (length, width, color, dashSize = 0.1) => {
        const points = [];
        const segments = 50; // Number of segments for the dashed effect
        
        // Top edge
        for (let i = 0; i <= segments; i++) {
          const x = (i / segments) * length - length / 2;
          points.push(new THREE.Vector3(x, 0.01, width / 2));
        }
        
        // Right edge
        for (let i = 0; i <= segments; i++) {
          const z = width / 2 - (i / segments) * width;
          points.push(new THREE.Vector3(length / 2, 0.01, z));
        }
        
        // Bottom edge
        for (let i = 0; i <= segments; i++) {
          const x = length / 2 - (i / segments) * length;
          points.push(new THREE.Vector3(x, 0.01, -width / 2));
        }
        
        // Left edge
        for (let i = 0; i <= segments; i++) {
          const z = -width / 2 + (i / segments) * width;
          points.push(new THREE.Vector3(-length / 2, 0.01, z));
        }
        
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        
        // Create a dashed line material
        const material = new THREE.LineDashedMaterial({
          color: color,
          dashSize: dashSize,
          gapSize: dashSize,
          linewidth: 2
        });
        
        const line = new THREE.Line(geometry, material);
        line.computeLineDistances(); // Required for dashed lines
        
        return line;
      };
      
      // Create felt footprint (inner, matches felt color)
      this.feltFootprint = createFootprintOutline(
        feltLength,
        feltWidth,
        this.feltColor, // Use current felt color
        0.08 // Slightly smaller dashes
      );
      this.scene.add(this.feltFootprint);
      
      // Create overall table footprint (outer, including rails, blue)
      this.tableFootprint = createFootprintOutline(
        tableLength,
        tableWidth,
        0x4a9eff, // Blue color for the overall table outline
        0.1
      );
      this.scene.add(this.tableFootprint);
    } catch (error) {
      console.error('Error creating table footprint:', error);
    }
  }
  
  frameFootprint() {
    if (!this.tableFootprint || !this.camera || !this.controls) return;
    
    // Calculate bounding box of the overall table footprint (includes rails)
    const box = new THREE.Box3().setFromObject(this.tableFootprint);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    // Position camera to view the footprint nicely from above
    const maxDim = Math.max(size.x, size.z);
    const distance = maxDim * 1.5;
    
    this.camera.position.set(
      center.x + distance * 0.5,
      center.y + distance * 0.8, // Higher angle to see the footprint
      center.z + distance * 0.5
    );
    this.camera.lookAt(center);
    
    if (this.controls) {
      this.controls.target.copy(center);
      this.controls.update();
    }
  }
  
  resetCameraToDefault() {
    if (!this.camera || !this.controls) return;
    
    // Reset camera to a nice default view that shows the grid
    this.camera.position.set(12, 8, 12);
    this.camera.lookAt(0, 0, 0);
    
    if (this.controls) {
      this.controls.target.set(0, 0, 0);
      this.controls.update();
    }
  }
  
  createPoolTable() {
    if (!this.scene) return;
    
    // Note: tableLength and tableWidth refer to the felt playing surface dimensions,
    // not the overall table size. Rails are added around the felt.
    const length = this.feetToMeters(this.params.tableLength);  // Felt length
    const width = this.feetToMeters(this.params.tableWidth);     // Felt width
    const height = this.inchesToMeters(this.params.tableHeightInches);
    
    // Validate dimensions
    if (isNaN(length) || isNaN(width) || isNaN(height) || length <= 0 || width <= 0 || height < 0) {
      throw new Error('Invalid table dimensions');
    }
    const surfaceThickness = 0.05; // 5cm felt surface
    const railHeight = 0.15; // 15cm rail height
    const railWidth = 0.08; // 8cm rail width
    
    this.tableGroup = new THREE.Group();
    
    // Table surface (felt) - green felt material
    // The felt is created with the specified dimensions (felt size)
    const feltGeometry = new THREE.BoxGeometry(length, surfaceThickness, width);
    const feltMaterial = new THREE.MeshStandardMaterial({ 
      color: this.feltColor, // Use stored felt color
      roughness: 0.9,
      metalness: 0.0,
      flatShading: true
    });
    const feltMesh = new THREE.Mesh(feltGeometry, feltMaterial);
    feltMesh.position.y = height;
    feltMesh.receiveShadow = true;
    this.feltMesh = feltMesh; // Store reference for color updates
    this.tableGroup.add(feltMesh);
    
    // Table rails (wood) - around the edges
    const railMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x8b4513, // Brown wood color
      roughness: 0.8,
      metalness: 0.0,
      flatShading: true
    });
    
    // Long rails (length dimension)
    const longRailGeometry = new THREE.BoxGeometry(length + railWidth * 2, railHeight, railWidth);
    const longRail1 = new THREE.Mesh(longRailGeometry, railMaterial);
    longRail1.position.set(0, height + railHeight / 2, width / 2 + railWidth / 2);
    longRail1.castShadow = true;
    this.tableGroup.add(longRail1);
    
    const longRail2 = new THREE.Mesh(longRailGeometry, railMaterial);
    longRail2.position.set(0, height + railHeight / 2, -width / 2 - railWidth / 2);
    longRail2.castShadow = true;
    this.tableGroup.add(longRail2);
    
    // Short rails (width dimension)
    const shortRailGeometry = new THREE.BoxGeometry(railWidth, railHeight, width);
    const shortRail1 = new THREE.Mesh(shortRailGeometry, railMaterial);
    shortRail1.position.set(length / 2 + railWidth / 2, height + railHeight / 2, 0);
    shortRail1.castShadow = true;
    this.tableGroup.add(shortRail1);
    
    const shortRail2 = new THREE.Mesh(shortRailGeometry, railMaterial);
    shortRail2.position.set(-length / 2 - railWidth / 2, height + railHeight / 2, 0);
    shortRail2.castShadow = true;
    this.tableGroup.add(shortRail2);
    
    // Table legs (simple wood posts)
    const legGeometry = new THREE.BoxGeometry(0.1, height, 0.1);
    const legPositions = [
      [length / 2 - 0.2, height / 2, width / 2 - 0.2],
      [length / 2 - 0.2, height / 2, -width / 2 + 0.2],
      [-length / 2 + 0.2, height / 2, width / 2 - 0.2],
      [-length / 2 + 0.2, height / 2, -width / 2 + 0.2],
    ];
    
    legPositions.forEach(([x, y, z]) => {
      const leg = new THREE.Mesh(legGeometry, railMaterial);
      leg.position.set(x, y, z);
      leg.castShadow = true;
      this.tableGroup.add(leg);
    });
    
    this.scene.add(this.tableGroup);
  }
  
  createProjector() {
    if (!this.scene) return;
    
    const tableLength = this.feetToMeters(this.params.tableLength);
    const tableWidth = this.feetToMeters(this.params.tableWidth);
    const tableHeight = this.inchesToMeters(this.params.tableHeightInches);
    const ceilingHeight = this.inchesToMeters(this.params.ceilingHeightInches);
    
    // Validate dimensions
    if (isNaN(tableLength) || isNaN(tableWidth) || isNaN(tableHeight) || isNaN(ceilingHeight)) {
      console.warn('Invalid projector parameters');
      return;
    }
    
    let projectorX = 0;
    let projectorY = 0;
    let projectorZ = 0;
    let rotationY = 0;
    
    // Calculate projector position based on mounting option
    if (this.params.mountingOption === 'ceiling') {
      // Above the long edge (length dimension) of the table, not centered
      projectorX = 0; // Center of table length
      projectorY = ceilingHeight - this.inchesToMeters(6);
      projectorZ = tableWidth / 2; // At the long edge (side of table), not center
      rotationY = -Math.PI / 2; // Rotate to point light cone toward table center
    } else if (this.params.mountingOption === 'side') {
      // On the side wall, centered on length, 60% up the wall
      projectorX = 0; // Center of table length
      projectorY = tableHeight + (ceilingHeight - tableHeight) * 0.6;
      projectorZ = tableWidth / 2 + 0.3; // Slightly off the table edge
      rotationY = Math.PI / 2; // Pointing toward table center
    } else if (this.params.mountingOption === 'end') {
      // At the end, centered on width, 60% up the wall
      projectorX = tableLength / 2 + 0.3; // Slightly off the table edge
      projectorY = tableHeight + (ceilingHeight - tableHeight) * 0.6;
      projectorZ = 0; // Center of table width
      rotationY = Math.PI; // Pointing toward table center
    }
    
    // Create projector (small box with indicator)
    const projectorSize = 0.15; // ~6 inches
    const projectorGeometry = new THREE.BoxGeometry(projectorSize, projectorSize * 0.6, projectorSize);
    // Create a group for the projector so we can rotate it properly
    this.projectorMesh = new THREE.Group();
    
    // Projector body - make base visible with different color
    const projectorBody = new THREE.Mesh(projectorGeometry, new THREE.MeshStandardMaterial({ 
      color: 0x333333, // Dark gray/black
      roughness: 0.7,
      metalness: 0.0
    }));
    
    // Paint the base (bottom face) with a different color to make it visible
    const baseMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x666666, // Lighter gray for base
      roughness: 0.7,
      metalness: 0.0
    });
    const baseGeometry = new THREE.PlaneGeometry(projectorSize, projectorSize);
    const base = new THREE.Mesh(baseGeometry, baseMaterial);
    base.rotation.x = -Math.PI / 2; // Face downward
    base.position.y = -projectorSize * 0.3; // At bottom of projector
    this.projectorMesh.add(base);
    this.projectorMesh.add(projectorBody);
    
    this.projectorMesh.position.set(projectorX, projectorY, projectorZ);
    this.projectorMesh.rotation.y = rotationY;
    this.projectorMesh.castShadow = true;
    this.scene.add(this.projectorMesh);
    
    // Add a small indicator cone showing projection direction (light cone pointing toward table center)
    const coneGeometry = new THREE.ConeGeometry(0.05, 0.2, 8);
    const coneMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xff6600, // Orange
      transparent: true,
      opacity: 0.7,
      roughness: 0.5,
      metalness: 0.0
    });
    const cone = new THREE.Mesh(coneGeometry, coneMaterial);
    
    if (this.params.mountingOption === 'ceiling') {
      // Point cone toward table center (down and toward center)
      // The projector is at the edge (Z = tableWidth/2), pointing toward center (Z = 0)
      // Calculate angle to point from edge toward center
      const distanceToCenter = tableWidth / 2;
      const heightAboveTable = projectorY - tableHeight;
      const angleToCenter = Math.atan2(distanceToCenter, heightAboveTable);
      // Point cone down and toward center
      cone.rotation.x = Math.PI / 2 - angleToCenter; // Angle down toward center
      cone.rotation.z = Math.PI / 2; // Rotate to point in -Z direction (toward center)
      cone.position.set(0, -0.15, 0);
    } else if (this.params.mountingOption === 'side') {
      cone.rotation.z = -Math.PI / 2; // Point left toward table center
      cone.position.set(-0.15, 0, 0);
    } else if (this.params.mountingOption === 'end') {
      cone.rotation.z = Math.PI; // Point back toward table center
      cone.position.set(0, 0, -0.15);
    }
    
    this.projectorMesh.add(cone);
  }
  
  frameScene() {
    if (!this.tableGroup) return;
    
    // Calculate bounding box
    const box = new THREE.Box3().setFromObject(this.tableGroup);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    // Position camera to view the scene nicely
    const maxDim = Math.max(size.x, size.y, size.z);
    const distance = maxDim * 2;
    
    this.camera.position.set(
      center.x + distance * 0.7,
      center.y + distance * 0.5,
      center.z + distance * 0.7
    );
    this.camera.lookAt(center);
    
    if (this.controls) {
      this.controls.target.copy(center);
      this.controls.update();
    }
  }
  
  animate() {
    this.animationFrameId = requestAnimationFrame(() => this.animate());
    
    if (this.controls) {
      this.controls.update();
    }
    
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }
  
  updateParams(params) {
    this.params = { ...this.params, ...params };
    this.updateScene();
  }
  
  destroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
    
    if (this.controls) {
      this.controls.dispose();
    }
    
    if (this.renderer) {
      this.renderer.dispose();
    }
    
    // Clean up geometries and materials
    if (this.tableGroup) {
      this.tableGroup.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
    }
    
    if (this.projectorMesh) {
      if (this.projectorMesh.geometry) this.projectorMesh.geometry.dispose();
      if (this.projectorMesh.material) this.projectorMesh.material.dispose();
    }
    
    if (this.tableFootprint) {
      if (this.tableFootprint.geometry) this.tableFootprint.geometry.dispose();
      if (this.tableFootprint.material) this.tableFootprint.material.dispose();
    }
    if (this.feltFootprint) {
      if (this.feltFootprint.geometry) this.feltFootprint.geometry.dispose();
      if (this.feltFootprint.material) this.feltFootprint.material.dispose();
    }
    
    if (this.axisLabelsGroup) {
      this.axisLabelsGroup.traverse((child) => {
        if (child.material) {
          if (child.material.map) child.material.map.dispose();
          child.material.dispose();
        }
      });
    }
    
    this.container.innerHTML = '';
  }
}

